import { getCommentHierarchyKey } from "@circle-react/components/CommentsV3/CommentHierarchy/useCommentHierarchy";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { commentCache } from "@circle-react/lib/queries/comments";
import { flaggedContentQueries } from "@circle-react/lib/queries/flaggedContent";
import { postInvalidations } from "@circle-react/lib/queries/posts";
import { type FlaggedContentableType } from "@circle-react/types";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { getCommentsListKey } from "@/react/components/CommentsV3/CommentsList/getCommentsListKey";
import { MESSAGE_REPORT_ACTIONS_QUERY_KEY } from "@/react/components/constants";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import {
  POST_TYPE,
  COMMENT_TYPE,
  CHAT_ROOM_MESSAGE_TYPE,
} from "@/react/types/FlaggedContent";

import {
  useModerationApi,
  type ModerationActionOptions,
} from "./useModerationApi";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

export const useApproveRejectContent = (
  flaggedContentId: string,
  flaggedContentType: FlaggedContentableType,
) => {
  const { success, error } = useToast();
  const { approve, reject } = useModerationApi();
  const queryClient = useQueryClient();

  const { data: flaggedContent, isInitialLoading } = useQuery({
    ...flaggedContentQueries.byContentable(
      flaggedContentId,
      flaggedContentType,
    ),
    enabled: !!flaggedContentId,
  });

  const contentId = flaggedContent?.id;

  const refetchRecord = (status: "approved" | "rejected") => {
    if (flaggedContentType === POST_TYPE) {
      const postId = flaggedContent?.flagged_contentable?.id;
      if (!postId) return;

      void postInvalidations.allPosts();
      void postInvalidations.postDetails(postId);
    } else if (flaggedContentType === COMMENT_TYPE) {
      const contentable = flaggedContent?.flagged_contentable;
      const commentablePostId = contentable?.post_id;
      const commentId = contentable?.id;

      void queryClient.refetchQueries({
        queryKey: getCommentsListKey({
          postId: commentablePostId,
          parentCommentId: contentable?.parent_comment_id,
        }),
      });
      void queryClient.refetchQueries({
        queryKey: getCommentHierarchyKey({
          postId: commentablePostId,
          id: flaggedContentId,
        }),
      });

      if (commentablePostId && commentId) {
        if (status === "approved") {
          commentCache.moderationApprove({
            postId: commentablePostId,
            id: commentId,
          });
        } else {
          commentCache.moderationReject({
            postId: commentablePostId,
            id: commentId,
            parentCommentId: contentable?.parent_comment_id ?? null,
          });
        }
      }
    } else if (flaggedContentType === CHAT_ROOM_MESSAGE_TYPE) {
      void queryClient.invalidateQueries({
        queryKey: [MESSAGE_REPORT_ACTIONS_QUERY_KEY],
      });
    }
  };

  const { mutate: approveFlaggedContent, isPending: isApproving } = useMutation<
    unknown,
    ApiError,
    { options?: ModerationActionOptions }
  >({
    mutationFn: ({ options }) => {
      if (!contentId) {
        throw new Error("Flagged content not found");
      }

      return approve(String(contentId), options);
    },
    onSuccess: () => {
      refetchRecord("approved");
      success(t([i18nRoot, "success_approving"]));
    },
    onError: () => error(t([i18nRoot, "error_approving"])),
  });

  const { mutate: rejectFlaggedContent, isPending: isRejecting } = useMutation<
    unknown,
    ApiError,
    { reason: string | null; options?: ModerationActionOptions }
  >({
    mutationFn: ({ reason, options }) => {
      if (!contentId) {
        throw new Error("Flagged content not found");
      }

      return reject(String(contentId), reason, options);
    },
    onSuccess: () => {
      refetchRecord("rejected");
      success(t([i18nRoot, "success_rejecting"]));
    },
    onError: () => error(t([i18nRoot, "error_rejecting"])),
  });

  return {
    approveFlaggedContent,
    isApproving,
    rejectFlaggedContent,
    isRejecting,
    isLoading: isInitialLoading,
    flaggedContent,
  };
};
